import { MaintenanceHistoryContext } from 'app/components/ReactContexts/maintenanceHistoryContext';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatter } from 'shared/formatters';
import { MaintenanceHistory } from 'types/MaintenanceHistory';
import { CompletedMaintenanceDialog } from '../Dialogs/CompleteMaintenanceTrack';
import { ReactComponent as ChevronRightIcon } from 'assets/images/chevron_right_gray.svg';
import { getMaintenanceIntervalTitle } from '../maintenanceUtils';
import { ListItem, ListItemText } from 'app/components/List';
import './MaintenanceHistoryListCard.less';
import { useSelectionContext } from 'app/components/bulk-actions';
import { Checkbox } from 'app/components/form-fields';

interface IMaintenanceListCardProps {
  maintenanceItem: MaintenanceHistory;
  index: number;
  key: any;
  openEngineHoursUpdateDialog: boolean;
  setOpenEngineHoursUpdateDialog: any;
  disabled: boolean;
  isSelectionEnabled: boolean;
  deleteMaintenanceHistory: () => void;
}

export function MaintenanceHistoryListCard(props: IMaintenanceListCardProps) {
  const { t } = useTranslation();
  const { formatDate, formatNumber } = useFormatter();

  const { maintenanceHistoryData } = React.useContext(
    MaintenanceHistoryContext,
  );

  const { selectedIds, toggle } = useSelectionContext<MaintenanceHistory>();
  const isSelected = selectedIds.has(props.maintenanceItem.id);

  const [maintenanceInterval, setMaintenanceInterval] = React.useState<any>();
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const title = getMaintenanceIntervalTitle(
    props.maintenanceItem.intervalType,
    props.maintenanceItem.intervalValue,
    t,
  );

  React.useEffect(() => {
    const allPossibleCheckpoints: any[] =
      maintenanceHistoryData.maintenanceIntervals?.flatMap(
        item => item.maintenanceCheckList,
      ) || [];

    const uniqueCheckpoints = Array.from(
      new Map(allPossibleCheckpoints.map(item => [item['id'], item])).values(),
    );

    const checks = uniqueCheckpoints.filter(obj =>
      props.maintenanceItem.maintenanceCheckList.hasOwnProperty(obj.id),
    );

    setMaintenanceInterval({
      id: props.maintenanceItem.id,
      intervalType: props.maintenanceItem.intervalType,
      intervalValue: props.maintenanceItem.intervalValue,
      maintenanceCheckList: checks,
    });
  }, [maintenanceHistoryData]);

  const handleClick = () => {
    if (props.disabled) return;
    maintenanceInterval && setIsDialogOpen(true);
  };
  const handleClose = () => setIsDialogOpen(false);

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (event.target.className === 'k-overlay') {
        event.stopPropagation();
        handleClose();
      }
    }

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  const renderCheckbox = () => {
    if (!props.isSelectionEnabled) return null;

    return (
      <div
        className="maintenance-history-list-card__checkbox-hitarea"
        onClick={e => {
          e.stopPropagation();
          toggle(props.maintenanceItem.id);
        }}
      >
        <div className="maintenance-history-list-card__checkbox-container">
          <Checkbox
            className="maintenance-history-list-card__checkbox"
            checked={isSelected}
            readOnly
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <ListItem
        startAdornment={renderCheckbox()}
        endAdornment={<ChevronRightIcon />}
        onClick={handleClick}
      >
        <ListItemText
          title={title}
          subtitle={
            <>
              <span className="maintenance-history-list-card__subtitle-item">
                {t('maintenance_history_status_hours_completion', {
                  engineHours: formatNumber(
                    props.maintenanceItem.completedEngineHours,
                  ),
                })}
              </span>
              <span>
                {t('maintenance_history_status_update', {
                  date: formatDate(props.maintenanceItem.updatedDate),
                })}
              </span>
            </>
          }
        />
      </ListItem>

      {isDialogOpen && (
        <CompletedMaintenanceDialog
          maintenanceInterval={maintenanceInterval}
          closeDialog={handleClose}
          action={'edit'}
          openEngineHoursUpdateDialog={props.openEngineHoursUpdateDialog}
          setOpenEngineHoursUpdateDialog={props.setOpenEngineHoursUpdateDialog}
          deleteMaintenanceHistory={props.deleteMaintenanceHistory}
        />
      )}
    </>
  );
}
